/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { ConfigException } from '../exceptions';
import { updateConstants, isNull, isValue } from '../helpers';
import { FileEnvironmentApi, LaunchdarklyRelay } from '../integration/';
import {
  ApiEndPointsNames,
  APIEnvs,
  ChannelType,
  DefaultAPIEnvs,
  IInitializeReturn,
  InitializeTypes,
  KeyValueStringType,
} from '../types';
import { Bundle } from '../util';
import { HeadersType } from '../util/constants';
import Base64 from '../util/Base64';
import AxiosHelper from './api/AxiosHelper';
import { updateEndpoints } from './api/Endpoints';
import { Logger } from './logger';
import { cleanMocks } from './mock_api';
import { AxiosInstance } from 'axios';

export type TypeOfAuth0 = {
  client_id: string;
  client_secret: string;
  audience: string;
  grant_type: string;
};

export const initialize = async (
  config: InitializeTypes,
): Promise<IInitializeReturn> => {
  const dtNow = new Date();
  let countErrors = 0;
  const retorno: IInitializeReturn = {
    success: true,
    environment: config.service.environment,
    channel: config.channel,
    isMock: false,
    isMockPartially: false,
    lstReady: [],
    lstMocksEnabled: [],
    timeStart: 0,
  };
  cleanMocks();
  Bundle.clearValue('mockAdapter');
  Bundle.clearValue('service-endpoint');
  Bundle.clearValue('service-environment');

  if (config.service.environment === 'MOCK') {
    retorno.isMock = true;
  }

  Bundle.setValue('config', config);

  /** Validando o Objeto de Configuração */
  try {
    validateInitialize(config);
    retorno.lstReady.push({
      name: 'validateConfig',
      success: true,
      errorMessage: '',
    });
  } catch (error: any) {
    retorno.lstReady.push({
      name: 'validateConfig',
      success: false,
      errorMessage: error.message,
    });
    countErrors++;
  }

  /** Fez Necessário remover a lib do BugSnag por incompatibilidade entre web e app
   * const environment = getEnvironmentFromConfig(config);
   * BugsnagHelper.configure(config.bugsnag, environment);
   * */

  /** Configurando o Axios e os Mocks caso necessário */

  try {
    const lstMocksEnabled = AxiosHelper.configure(config);
    retorno.lstReady.push({
      name: 'axios',
      success: true,
      errorMessage: '',
    });
    if (config.service.environment === 'MOCK') {
      retorno.isMock = true;
      retorno.lstReady.push({
        name: 'mock',
        success: true,
        errorMessage: '',
      });
      retorno.lstMocksEnabled = lstMocksEnabled;
    } else if (lstMocksEnabled.length > 0) {
      retorno.isMockPartially = true;
      retorno.lstReady.push({
        name: 'mock',
        success: true,
        errorMessage: '',
      });
      retorno.lstMocksEnabled = lstMocksEnabled;
    }
  } catch (error: any) {
    let errorAxios = '';
    let errorMock = '';

    if (error.resource === 'axios') {
      errorAxios = error.message;
    } else if (error.resource === 'mock') {
      errorMock = error.message;
    }
    retorno.lstReady.push({
      name: 'axios',
      success: false,
      errorMessage: errorAxios,
    });
    retorno.lstReady.push({
      name: 'mock',
      success: false,
      errorMessage: errorMock,
    });
    countErrors++;
  }

  let isSuccessEnvsAndConst = false;
  /** Configurando As Constantes e Endpoints do launchdarkly */
  try {
    const configureEnvsAndConstStatus = await configureEnvsAndConst(
      config.service.environment,
      config.channel,
    );
    isSuccessEnvsAndConst = configureEnvsAndConstStatus ? true : false;

    retorno.lstReady.push({
      name: 'EndPoinstConstantsLaunchdarkly',
      success: isSuccessEnvsAndConst,
      errorMessage: '',
    });
    retorno.environment = configureEnvsAndConstStatus;
  } catch (error: any) {
    retorno.lstReady.push({
      name: 'EndPoinstConstantsLaunchdarkly',
      success: false,
      errorMessage: error.message,
    });
    countErrors++;
  }

  /** Configurando Token não logado - geração de token removido da inicialização */

  if (countErrors !== 0) {
    retorno.success = false;
  } else {
    generateMsgConfigSuccess(config);
  }

  retorno.timeStart = calculateTime(dtNow);
  return retorno;
};

type tokenDataType = {
  accessToken: string;
};

export const saveTokenAuthUser = (tokenData: tokenDataType) =>
  AxiosHelper.saveTokens(JSON.stringify(tokenData), 'TOKEN_AUTH_USER');

export const removeTokenAuthUser = () =>
  AxiosHelper.removeTokens('TOKEN_AUTH_USER');

export const saveTokenSmiles = (token: string) =>
  AxiosHelper.saveSmilesToken(token);

export const removeTokenSmiles = () => AxiosHelper.removeSmilesToken();

export const getMemberNumberByAuthUser = (): string | null =>
  AxiosHelper.getMemberNumberByAuthUser();

export const axiosInstance: AxiosInstance =
  AxiosHelper.getAxiosInstance() as AxiosInstance;

export const getUrlAndHeaderByType = (
  apiName: ApiEndPointsNames,
  path: string,
  pathParameters: KeyValueStringType,
  pathUrl?: string | undefined | null,
) => AxiosHelper.getUrlAndHeaderByType(apiName, path, pathParameters, pathUrl);

const configureEnvsAndConst = async (
  environment: APIEnvs,
  channel: ChannelType,
) => {
  try {
    let environmentLD = environment.valueOf();
    if (environmentLD === 'MOCK') {
      configureTokens({
        [HeadersType.xApiKey]: 'x-api-token',
      });
      return environmentLD;
    }

    if (environment === 'PRD') {
      const fileEnvironmentApiResponse = await FileEnvironmentApi.call();
      if (
        fileEnvironmentApiResponse.success &&
        isValue(fileEnvironmentApiResponse.data)
      ) {
        environmentLD = fileEnvironmentApiResponse.data;
      }
    }
    environmentLD = environmentLD.toUpperCase();

    const mapUsers = new Map();
    Object.entries(DefaultAPIEnvs).forEach(([, value]) =>
      value.forEach((v: string | number) => {
        const environmentAux = String(v);
        if (environmentAux === 'PRD') {
          mapUsers.set('BLUE', 'eyJrZXkiOiJCTFVFIn0=');
          mapUsers.set('GREEN', 'eyJrZXkiOiJHUkVFTiJ9');
        } else {
          const b64 = Base64.btoa(`{"key":"${environmentAux}"}`);
          mapUsers.set(environmentAux, b64);
        }
      }),
    );

    const request = {
      idAplication: '5ff64893991a4c0aa4974fc7',
      userBase64: mapUsers.get(environmentLD),
    };

    const launchdarklyResponse = await LaunchdarklyRelay.call(
      request,
      environmentLD,
    );
    if (launchdarklyResponse.success && launchdarklyResponse.data) {
      const { constants, endpoints } = launchdarklyResponse.data;
      if (constants) {
        const constantsChannel = constants[channel];
        if (constantsChannel) {
          updateConstants(environment, constantsChannel);
          configureTokens(constantsChannel);
        } else {
          throw new ConfigException({
            message: `[Relay/Launchdarkly] - Dados não encontrados para o canal ${channel} no embiente ${environment}`,
            resource: 'EndPoinstConstantsLaunchdarkly',
          });
        }
      }
      if (endpoints) {
        updateEndpoints(environment, endpoints);
      }
      return environmentLD;
    } else {
      throw new ConfigException({
        message: '[Relay/Launchdarkly] - Erro na requisição',
        resource: 'EndPoinstConstantsLaunchdarkly',
      });
    }
  } catch (error: any) {
    Logger.error(error.message);
    throw new ConfigException({
      message: error.message,
      resource: 'EndPoinstConstantsLaunchdarkly',
    });
  }
};

const configureTokens = (dados: any) => {
  if (isValue(dados[HeadersType.xApiKey])) {
    const xApiKey = dados[HeadersType.xApiKey];
    AxiosHelper.saveTokens(JSON.stringify(xApiKey), 'TOKEN_X_API_KEY');
  }
};

const generateMsgConfigSuccess = (config: InitializeTypes) => {
  let msg = '\n====================================================\n\n';
  msg += 'Core [Engine] successfully configured !!!\n';
  msg += `Environment : ${config.service.environment} \n`;
  msg += '\n====================================================\n\n';
  Logger.log(msg);
};

const validateInitialize = (config: InitializeTypes) => {
  if (isNull(config)) {
    throw new Error('Config not found');
  }
  if (isNull(config.service)) {
    throw new Error(
      'service not found, Example service : { environment: `DEV`, logger: true } ',
    );
  }
  if (isNull(config.service.environment)) {
    throw new Error(
      'service:environment not found, Example (DEV1, DEV2, HML1)',
    );
  }
  if (isNull(config.channel)) {
    throw new Error('channel not found, Example (APP,WEB)');
  }
  if (isNull(config.loggerXstate)) {
    throw new Error(
      'loggerXstate not found, Example loggerXstate : true or false',
    );
  }
};

const calculateTime = (timeStart: Date) => {
  let milliseconds = 0;
  try {
    const end = new Date();
    milliseconds = end.getTime() - timeStart.getTime();
  } catch (error) {
    Logger.info(error);
  }
  return milliseconds;
};
